import React from "react";
import { Command, Loader2 } from "lucide-react";

import { BrandButton } from "@/shared/components/ui-kit";
import usePublishFlow from "../hooks/usePublishFlow";

import type { Agent } from "@/shared/types";

const shortcut = (
  <span className="flex items-center gap-[1px] h-[18px] px-[5px] rounded-[5px] bg-[color-mix(in_srgb,var(--brand-foreground)_13%,transparent)] ring-1 ring-[color-mix(in_srgb,var(--brand-foreground)_12%,transparent)] text-[11px] font-mono font-medium">
    <Command className="w-[9px] h-[9px]" strokeWidth={2.25} />S
  </span>
);

/* The header over every section of an agent's Settings, whichever is open:
   where the draft stands, and Publish. An agent's edits land in its draft as they
   are made; runs read the published version, so the action is Publish, live only
   while there is something to publish, through `usePublishFlow` (evals first when
   the agent asks for them). ⌘S publishes too. */
export default function PublishBar({ agent }: { agent: Agent }) {
  const { start, publishing, changed, gated, dialog } = usePublishFlow(agent);
  const commit = React.useRef(start);
  commit.current = start;

  React.useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "s") {
        e.preventDefault();
        commit.current();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  return (
    <div className="h-14 shrink-0 flex items-center gap-3 px-4 md:px-8 border-b hairline">
      <div className="min-w-0 flex-1 flex items-center gap-1.5 text-[12.5px] text-[var(--text-tertiary)] whitespace-nowrap">
        {changed ? <><span className="shrink-0 w-1.5 h-1.5 rounded-full bg-[var(--warn)]" /><span className="text-[var(--text-secondary)]">Unpublished changes</span></>
          : <><span className="font-mono tabular-nums">{agent.version}</span> is live</>}
      </div>
      <div className="flex items-center gap-3">
        <BrandButton
          onClick={start}
          disabled={publishing || !changed}
          title={`Publish the draft as the version runs use (⌘S)${gated ? " — its eval cases run on it first" : ""}`}
          className="disabled:opacity-40 disabled:pointer-events-none"
        >
          {publishing ? <><Loader2 className="w-3 h-3 animate-spin" strokeWidth={2.25} />Publishing</> : <>Publish{shortcut}</>}
        </BrandButton>
        {dialog}
      </div>
    </div>
  );
}
